/**
 * Professional Authentication Dialogs for DeckOps.ai
 * EXACT implementation from _ui_upgrade templates
 */

import { auth, db, firebase } from '../firebaseConfig.js';
import { MobileDetectionService } from '../services/MobileDetectionService.js';

class AuthenticationDialogs {
    constructor() {
        this.currentStep = 1;
        this.signupData = {};
        this.init();
    }

    init() {
        this.createDialogHTML();
        this.bindEvents();
        console.log('🎨 Professional Authentication Dialogs initialized (CLEAN VERSION)');
    }

    createDialogHTML() {
        // Create the dialog container
        const dialogContainer = document.createElement('div');
        dialogContainer.id = 'auth-dialogs-container';
        dialogContainer.innerHTML = this.getDialogHTML();
        
        // Add to body
        document.body.appendChild(dialogContainer);

        // Add styles
        this.addStyles();
    }

    getDialogHTML() {
        // Detect mobile for responsive templates
        const isMobile = MobileDetectionService?.isMobile() || window.innerWidth <= 768;
        console.log(`🎨 AuthenticationDialogs: Creating ${isMobile ? 'mobile' : 'desktop'} templates`);
        
        return `
            <!-- Sign In Dialog - Responsive Template -->
            <div class="auth-overlay ${isMobile ? 'mobile-auth' : 'desktop-auth'}" id="signInOverlay">
                <div class="signin-background"></div>
                <div class="signin-background-grid"></div>
                
                <div class="signin-card">
                    <!-- Logo Section -->
                    <div class="signin-logo-section">
                        <div class="signin-logo-container">
                            <div class="signin-logo">D</div>
                            <div class="signin-brand-text">
                                <div class="signin-brand-name">DeckOps.ai</div>
                                <div class="signin-brand-tagline">EXECUTIVE PLATFORM</div>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Form Header -->
                    <div class="signin-form-header">
                        <h1 class="signin-form-title">Welcome back</h1>
                        <p class="signin-form-subtitle">Sign in to access your executive dashboard</p>
                    </div>
                    
                    <!-- Sign In Form -->
                    <form id="signInForm">
                        <!-- Email Field -->
                        <div class="signin-form-group">
                            <label class="signin-form-label" for="signInEmail">Corporate Email</label>
                            <div class="signin-input-wrapper">
                                <svg class="signin-input-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                                </svg>
                                <input 
                                    type="email" 
                                    id="signInEmail"
                                    class="signin-form-input" 
                                    placeholder="john.doe@company.com" 
                                    required
                                    autocomplete="email"
                                >
                            </div>
                        </div>
                        
                        <!-- Password Field -->
                        <div class="signin-form-group">
                            <label class="signin-form-label" for="signInPassword">Password</label>
                            <div class="signin-input-wrapper">
                                <svg class="signin-input-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
                                </svg>
                                <input 
                                    type="password" 
                                    id="signInPassword"
                                    class="signin-form-input" 
                                    placeholder="Enter your password" 
                                    required
                                    autocomplete="current-password"
                                >
                                <button type="button" class="signin-password-toggle" onclick="window.authDialogs.togglePassword()" aria-label="Toggle password visibility">
                                    <svg id="signInEyeIcon" fill="none" stroke="currentColor" viewBox="0 0 24 24" width="18" height="18">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                                    </svg>
                                </button>
                            </div>
                        </div>
                        
                        <!-- Form Options -->
                        <div class="signin-form-options">
                            <div class="signin-checkbox-group">
                                <input type="checkbox" id="rememberMe" class="signin-checkbox-input">
                                <label for="rememberMe" class="signin-checkbox-label">Remember me</label>
                            </div>
                            <a href="#" class="signin-forgot-link">Forgot password?</a>
                        </div>
                        
                        <!-- Submit Button -->
                        <button type="submit" class="signin-btn-submit">
                            <span class="signin-btn-text">Sign In</span>
                            <span class="signin-loading"></span>
                        </button>
                    </form>
                    
                    <!-- Divider -->
                    <div class="signin-divider">
                        <div class="signin-divider-line"></div>
                        <span class="signin-divider-text">Or continue with</span>
                        <div class="signin-divider-line"></div>
                    </div>
                    
                    <!-- Social Buttons -->
                    <div class="signin-social-buttons">
                        <button class="signin-btn-social">
                            <svg class="signin-social-icon" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                            </svg>
                            <span>Google</span>
                        </button>
                        
                        <button class="signin-btn-social">
                            <svg class="signin-social-icon" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.79M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z"/>
                            </svg>
                            <span>LinkedIn</span>
                        </button>
                    </div>
                    
                    <!-- Footer -->
                    <div class="signin-footer">
                        <p class="signin-footer-text">
                            New to DeckOps? <a href="#" class="signin-footer-link" onclick="window.authDialogs.showSignUp()">Create an account</a>
                        </p>
                    </div>
                    
                    <!-- Security Badge -->
                    <div class="signin-security-badge">
                        <svg class="signin-security-icon" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"/>
                        </svg>
                        <span>Enterprise-grade security • SOC 2 Type II</span>
                    </div>
                </div>
                
                <!-- Enterprise Features -->
                <div class="signin-enterprise-features">
                    <a href="#">Terms</a>
                    <span>•</span>
                    <a href="#">Privacy</a>
                    <span>•</span>
                    <a href="#">Security</a>
                    <span>•</span>
                    <a href="#">Contact</a>
                </div>
            </div>

            <!-- Sign Up Dialog - EXACT desktop-signup-enterprise.html Template -->
            <div class="auth-overlay" id="signUpOverlay">
                <div class="signup-background"></div>
                <div class="signup-orb signup-orb-1"></div>
                <div class="signup-orb signup-orb-2"></div>
                
                <div class="signup-card">
                    <!-- Card Header -->
                    <div class="signup-card-header">
                        <div class="signup-header-content">
                            <div class="signup-logo-section">
                                <div class="signup-logo">D</div>
                                <div class="signup-brand-name">DeckOps.ai</div>
                            </div>
                            <button class="signup-close-btn" onclick="window.authDialogs.closeDialog()">×</button>
                        </div>
                    </div>
                    
                    <!-- Progress Bar -->
                    <div class="signup-progress-bar">
                        <div class="signup-progress-steps">
                            <div class="signup-progress-line">
                                <div class="signup-progress-line-fill" id="signupProgressFill" style="width: 0%"></div>
                            </div>
                            
                            <div class="signup-step active" id="signupStep1">
                                <div class="signup-step-number"><span>1</span></div>
                                <div class="signup-step-label">Account</div>
                            </div>
                            
                            <div class="signup-step" id="signupStep2">
                                <div class="signup-step-number"><span>2</span></div>
                                <div class="signup-step-label">Personal</div>
                            </div>
                            
                            <div class="signup-step" id="signupStep3">
                                <div class="signup-step-number"><span>3</span></div>
                                <div class="signup-step-label">Professional</div>
                            </div>
                            
                            <div class="signup-step" id="signupStep4">
                                <div class="signup-step-number"><span>4</span></div>
                                <div class="signup-step-label">Verification</div>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Card Body -->
                    <div class="signup-card-body">
                        <form id="signupForm">
                            <!-- Step 1: Account -->
                            <div class="signup-form-step active" id="signupFormStep1">
                                <h2 class="signup-step-title">Create your executive account</h2>
                                <p class="signup-step-description">Join thousands of Fortune 500 executives and consultants</p>
                                
                                <div class="signup-form-group signup-full-width">
                                    <label class="signup-form-label">Corporate Email <span class="signup-required">*</span></label>
                                    <div class="signup-input-wrapper">
                                        <svg class="signup-input-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                                        </svg>
                                        <input type="email" class="signup-form-input signup-with-icon" id="signUpEmail" placeholder="john.doe@company.com" required>
                                    </div>
                                </div>
                                
                                <div class="signup-form-row">
                                    <div class="signup-form-group">
                                        <label class="signup-form-label">Password <span class="signup-required">*</span></label>
                                        <input type="password" class="signup-form-input" id="signUpPassword" placeholder="Minimum 12 characters" required>
                                    </div>
                                    
                                    <div class="signup-form-group">
                                        <label class="signup-form-label">Confirm Password <span class="signup-required">*</span></label>
                                        <input type="password" class="signup-form-input" id="signUpPasswordConfirm" placeholder="Re-enter password" required>
                                    </div>
                                </div>
                                
                                <div class="signup-password-requirements">
                                    <div class="signup-requirement" id="signupReqLength">
                                        <svg class="signup-requirement-icon" fill="currentColor" viewBox="0 0 20 20">
                                            <circle cx="10" cy="10" r="8" fill="none" stroke="currentColor" stroke-width="2"/>
                                        </svg>
                                        <span>At least 12 characters</span>
                                    </div>
                                    <div class="signup-requirement" id="signupReqUpper">
                                        <svg class="signup-requirement-icon" fill="currentColor" viewBox="0 0 20 20">
                                            <circle cx="10" cy="10" r="8" fill="none" stroke="currentColor" stroke-width="2"/>
                                        </svg>
                                        <span>One uppercase letter</span>
                                    </div>
                                    <div class="signup-requirement" id="signupReqNumber">
                                        <svg class="signup-requirement-icon" fill="currentColor" viewBox="0 0 20 20">
                                            <circle cx="10" cy="10" r="8" fill="none" stroke="currentColor" stroke-width="2"/>
                                        </svg>
                                        <span>One number or symbol</span>
                                    </div>
                                </div>
                            </div>

                            <!-- Step 2: Personal -->
                            <div class="signup-form-step" id="signupFormStep2">
                                <h2 class="signup-step-title">Personal information</h2>
                                <p class="signup-step-description">Help us personalize your experience</p>
                                
                                <div class="signup-form-row">
                                    <div class="signup-form-group">
                                        <label class="signup-form-label">First Name <span class="signup-required">*</span></label>
                                        <input type="text" class="signup-form-input" id="signUpFirstName" placeholder="John" required>
                                    </div>
                                    
                                    <div class="signup-form-group">
                                        <label class="signup-form-label">Last Name <span class="signup-required">*</span></label>
                                        <input type="text" class="signup-form-input" id="signUpLastName" placeholder="Doe" required>
                                    </div>
                                </div>
                                
                                <div class="signup-form-row">
                                    <div class="signup-form-group">
                                        <label class="signup-form-label">Phone Number</label>
                                        <input type="tel" class="signup-form-input" id="signUpPhone" placeholder="+1 (555) 000-0000">
                                    </div>
                                    
                                    <div class="signup-form-group">
                                        <label class="signup-form-label">LinkedIn Profile</label>
                                        <input type="url" class="signup-form-input" id="signUpLinkedIn" placeholder="linkedin.com/in/johndoe">
                                    </div>
                                </div>
                            </div>

                            <!-- Step 3: Professional -->
                            <div class="signup-form-step" id="signupFormStep3">
                                <h2 class="signup-step-title">Professional details</h2>
                                <p class="signup-step-description">Select your company and role</p>
                                
                                <label class="signup-form-label">Select Your Company</label>
                                <div class="signup-company-grid">
                                    <div class="signup-company-card" onclick="window.authDialogs.selectCompany(this)" data-company="mckinsey">
                                        <div class="signup-company-icon">🏢</div>
                                        <div class="signup-company-name">McKinsey</div>
                                        <div class="signup-company-type">Consulting</div>
                                    </div>
                                    <div class="signup-company-card" onclick="window.authDialogs.selectCompany(this)" data-company="bain">
                                        <div class="signup-company-icon">🏢</div>
                                        <div class="signup-company-name">Bain</div>
                                        <div class="signup-company-type">Consulting</div>
                                    </div>
                                    <div class="signup-company-card" onclick="window.authDialogs.selectCompany(this)" data-company="bcg">
                                        <div class="signup-company-icon">🏢</div>
                                        <div class="signup-company-name">BCG</div>
                                        <div class="signup-company-type">Consulting</div>
                                    </div>
                                    <div class="signup-company-card" onclick="window.authDialogs.selectCompany(this)" data-company="other">
                                        <div class="signup-company-icon">➕</div>
                                        <div class="signup-company-name">Other</div>
                                        <div class="signup-company-type">Custom</div>
                                    </div>
                                </div>
                                
                                <div class="signup-form-row">
                                    <div class="signup-form-group">
                                        <label class="signup-form-label">Job Title <span class="signup-required">*</span></label>
                                        <input type="text" class="signup-form-input" id="signUpJobTitle" placeholder="Senior Consultant" required>
                                    </div>
                                    
                                    <div class="signup-form-group">
                                        <label class="signup-form-label">Department</label>
                                        <input type="text" class="signup-form-input" id="signUpDepartment" placeholder="Strategy & Operations">
                                    </div>
                                </div>
                            </div>

                            <!-- Step 4: Verification -->
                            <div class="signup-form-step" id="signupFormStep4">
                                <div class="signup-success-message">
                                    <div class="signup-success-icon">✓</div>
                                    <h2 class="signup-step-title">Almost there!</h2>
                                    <p class="signup-step-description">
                                        We've sent a verification email to your corporate address.<br>
                                        Please check your inbox and click the verification link to activate your account.
                                    </p>
                                </div>
                            </div>
                            
                            <!-- Navigation -->
                            <div class="signup-form-navigation" id="signupNavigation">
                                <button type="button" class="signup-btn signup-btn-secondary" id="signupPrevBtn" onclick="window.authDialogs.prevStep()" style="display:none">
                                    Previous
                                </button>
                                <button type="button" class="signup-btn signup-btn-primary" id="signupNextBtn" onclick="window.authDialogs.nextStep()">
                                    Continue
                                </button>
                            </div>
                        </form>
                    </div>
                    
                    <!-- Card Footer -->
                    <div class="signup-card-footer">
                        <p class="signup-footer-text">
                            Already have an account? <a href="#" class="signup-footer-link" onclick="window.authDialogs.showSignIn()">Sign in</a>
                        </p>
                    </div>
                </div>
            </div>
        `;
    }

    addStyles() {
        if (document.getElementById('auth-dialog-styles')) return;

        const style = document.createElement('style');
        style.id = 'auth-dialog-styles';
        style.textContent = `
            /* EXACT CSS from desktop-signin-enterprise.html + desktop-signup-enterprise.html templates */
            * {
                margin: 0;
                padding: 0;
                box-sizing: border-box;
                -webkit-font-smoothing: antialiased;
                -moz-osx-font-smoothing: grayscale;
            }
            
            :root {
                /* Premium Enterprise Color System */
                --primary: #00d4ff;
                --primary-rgb: 0, 212, 255;
                --primary-dark: #0ea5e9;
                --primary-darker: #0284c7;
                --background: #0f172a;
                --background-secondary: #1e293b;
                --surface: rgba(30, 41, 59, 0.5);
                --surface-elevated: rgba(30, 41, 59, 0.8);
                --glass: rgba(255, 255, 255, 0.02);
                --glass-heavy: rgba(255, 255, 255, 0.05);
                --border: rgba(255, 255, 255, 0.08);
                --border-strong: rgba(255, 255, 255, 0.12);
                --text-primary: #ffffff;
                --text-secondary: rgba(255, 255, 255, 0.8);
                --text-muted: rgba(148, 163, 184, 1);
                --success: #10b981;
                --warning: #f59e0b;
                --error: #ef4444;
                --shadow-xl: 0 20px 50px rgba(0, 0, 0, 0.5);
                --shadow-glow: 0 0 100px rgba(0, 212, 255, 0.1);
            }
            
            .auth-overlay {
                font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Segoe UI', system-ui, sans-serif;
                background: var(--glass-bg, rgba(15, 23, 42, 0.6));
                backdrop-filter: blur(20px);
                -webkit-backdrop-filter: blur(20px);
                color: var(--text-primary, #ffffff);
                position: fixed;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                display: none;
                align-items: center;
                justify-content: center;
                z-index: 100000;
                opacity: 0;
                visibility: hidden;
                transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                overflow: hidden;
            }

            .auth-overlay.active {
                display: flex !important;
                opacity: 1 !important;
                visibility: visible !important;
            }

            /* ====== SIGN IN STYLES (480px centered) ====== */
            
            /* Premium Background */
            .signin-background {
                position: fixed;
                inset: 0;
                background: 
                    radial-gradient(circle at 20% 50%, rgba(0, 212, 255, 0.1) 0%, transparent 50%),
                    radial-gradient(circle at 80% 80%, rgba(14, 165, 233, 0.08) 0%, transparent 50%),
                    radial-gradient(circle at 50% 50%, var(--background-secondary) 0%, var(--background) 100%);
                z-index: -2;
            }
            
            .signin-background-grid {
                position: fixed;
                inset: 0;
                background-image: 
                    linear-gradient(rgba(0, 212, 255, 0.01) 1px, transparent 1px),
                    linear-gradient(90deg, rgba(0, 212, 255, 0.01) 1px, transparent 1px);
                background-size: 50px 50px;
                z-index: -1;
                animation: gridMove 30s linear infinite;
            }
            
            @keyframes gridMove {
                0% { transform: translate(0, 0); }
                100% { transform: translate(50px, 50px); }
            }
            
            /* Sign In Card - Perfectly Balanced */
            .signin-card {
                width: 100%;
                max-width: 480px;
                background: var(--glass-bg-elevated, rgba(30, 41, 59, 0.8));
                backdrop-filter: blur(40px);
                -webkit-backdrop-filter: blur(40px);
                border: 1px solid var(--glass-border, rgba(255, 255, 255, 0.1));
                border-radius: 24px;
                padding: 48px;
                box-shadow: 
                    var(--shadow-glass, 0 25px 50px -12px rgba(0, 0, 0, 0.25)),
                    var(--shadow-glow, 0 0 0 1px rgba(255, 255, 255, 0.05)),
                    inset 0 1px 1px rgba(255, 255, 255, 0.05);
                position: relative;
                overflow: hidden;
                animation: cardEntry 0.8s cubic-bezier(0.21, 1.02, 0.73, 1);
            }
            
            @keyframes cardEntry {
                from {
                    opacity: 0;
                    transform: scale(0.95) translateY(20px);
                }
                to {
                    opacity: 1;
                    transform: scale(1) translateY(0);
                }
            }
            
            /* Premium Glass Effect */
            .signin-card::before {
                content: '';
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                height: 1px;
                background: linear-gradient(90deg, 
                    transparent, 
                    rgba(255, 255, 255, 0.1), 
                    transparent);
                animation: shimmer 3s infinite;
            }
            
            @keyframes shimmer {
                0% { transform: translateX(-100%); }
                100% { transform: translateX(100%); }
            }
            
            /* Logo Section */
            .signin-logo-section {
                text-align: center;
                margin-bottom: 40px;
                animation: fadeIn 0.6s ease;
            }
            
            @keyframes fadeIn {
                from { opacity: 0; }
                to { opacity: 1; }
            }
            
            .signin-logo-container {
                display: inline-flex;
                align-items: center;
                gap: 16px;
                margin-bottom: 24px;
            }
            
            .signin-logo {
                width: 56px;
                height: 56px;
                background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
                border-radius: 16px;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 28px;
                font-weight: 800;
                color: var(--background);
                box-shadow: 
                    0 10px 30px rgba(var(--primary-rgb), 0.3),
                    inset 0 1px 1px rgba(255, 255, 255, 0.3);
                position: relative;
            }
            
            .signin-logo::after {
                content: '';
                position: absolute;
                inset: 0;
                border-radius: 16px;
                background: linear-gradient(135deg, transparent 40%, rgba(255, 255, 255, 0.2));
                pointer-events: none;
            }
            
            .signin-brand-text {
                text-align: left;
            }
            
            .signin-brand-name {
                font-size: 24px;
                font-weight: 700;
                letter-spacing: -0.5px;
                margin-bottom: 2px;
                background: linear-gradient(135deg, var(--text-primary) 0%, var(--primary) 100%);
                -webkit-background-clip: text;
                background-clip: text;
                -webkit-text-fill-color: transparent;
            }
            
            .signin-brand-tagline {
                font-size: 12px;
                color: var(--text-muted);
                letter-spacing: 0.5px;
                font-weight: 500;
            }
            
            /* Form Header */
            .signin-form-header {
                text-align: center;
                margin-bottom: 32px;
            }
            
            .signin-form-title {
                font-size: 28px;
                font-weight: 600;
                letter-spacing: -0.5px;
                margin-bottom: 8px;
                color: var(--text-primary);
            }
            
            .signin-form-subtitle {
                font-size: 14px;
                color: var(--text-secondary);
                line-height: 1.5;
            }
            
            /* Form Elements */
            .signin-form-group {
                margin-bottom: 24px;
            }
            
            .signin-form-label {
                display: block;
                font-size: 12px;
                font-weight: 600;
                color: var(--text-secondary);
                text-transform: uppercase;
                letter-spacing: 0.8px;
                margin-bottom: 8px;
            }
            
            .signin-input-wrapper {
                position: relative;
            }
            
            .signin-form-input {
                width: 100%;
                height: 48px;
                padding: 0 16px 0 44px;
                background: var(--glass-bg, rgba(30, 41, 59, 0.6));
                border: 1px solid var(--glass-border, rgba(255, 255, 255, 0.1));
                border-radius: 12px;
                color: var(--text-primary, #ffffff);
                font-size: 15px;
                font-weight: 400;
                transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            }
            
            .signin-form-input:hover {
                background: rgba(255, 255, 255, 0.06);
            }
            
            .signin-form-input:focus {
                outline: none;
                background: rgba(255, 255, 255, 0.08);
                border-color: var(--primary, #00d4ff);
                box-shadow: 
                    0 0 0 3px rgba(0, 212, 255, 0.1),
                    0 4px 12px rgba(0, 212, 255, 0.05);
            }
            
            .signin-form-input::placeholder {
                color: var(--text-secondary, rgba(255, 255, 255, 0.7));
            }
            
            .signin-input-icon {
                position: absolute;
                left: 16px;
                top: 50%;
                transform: translateY(-50%);
                width: 18px;
                height: 18px;
                color: var(--text-muted);
                transition: color 0.3s ease;
            }
            
            .signin-form-group:focus-within .signin-input-icon {
                color: var(--primary);
            }
            
            .signin-password-toggle {
                position: absolute;
                right: 12px;
                top: 50%;
                transform: translateY(-50%);
                width: 32px;
                height: 32px;
                display: flex;
                align-items: center;
                justify-content: center;
                background: transparent;
                border: none;
                color: var(--text-muted);
                cursor: pointer;
                border-radius: 8px;
                transition: all 0.2s ease;
            }
            
            .signin-password-toggle:hover {
                background: var(--glass);
                color: var(--text-secondary);
            }
            
            /* Form Options */
            .signin-form-options {
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 32px;
            }
            
            .signin-checkbox-group {
                display: flex;
                align-items: center;
                gap: 8px;
            }
            
            .signin-checkbox-input {
                width: 18px;
                height: 18px;
                accent-color: var(--primary);
                cursor: pointer;
            }
            
            .signin-checkbox-label {
                font-size: 14px;
                color: var(--text-secondary);
                cursor: pointer;
                user-select: none;
            }
            
            .signin-forgot-link {
                font-size: 14px;
                font-weight: 500;
                color: var(--primary);
                text-decoration: none;
                transition: color 0.2s ease;
            }
            
            .signin-forgot-link:hover {
                color: var(--primary-dark);
            }
            
            /* Submit Button */
            .signin-btn-submit {
                width: 100%;
                height: 48px;
                background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
                border: none;
                border-radius: 12px;
                color: var(--background);
                font-size: 15px;
                font-weight: 700;
                letter-spacing: 0.3px;
                cursor: pointer;
                position: relative;
                overflow: hidden;
                transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                box-shadow: 
                    0 4px 14px rgba(var(--primary-rgb), 0.4),
                    inset 0 1px 1px rgba(255, 255, 255, 0.2);
            }
            
            .signin-btn-submit:hover {
                transform: translateY(-1px);
                box-shadow: 
                    0 6px 20px rgba(var(--primary-rgb), 0.5),
                    inset 0 1px 1px rgba(255, 255, 255, 0.2);
            }
            
            .signin-btn-submit:active {
                transform: translateY(0);
            }
            
            /* Ripple Effect */
            .signin-btn-submit::before {
                content: '';
                position: absolute;
                top: 50%;
                left: 50%;
                width: 0;
                height: 0;
                border-radius: 50%;
                background: rgba(255, 255, 255, 0.3);
                transform: translate(-50%, -50%);
                transition: width 0.6s ease, height 0.6s ease;
            }
            
            .signin-btn-submit:active::before {
                width: 300px;
                height: 300px;
            }
            
            /* Divider */
            .signin-divider {
                display: flex;
                align-items: center;
                gap: 16px;
                margin: 32px 0;
            }
            
            .signin-divider-line {
                flex: 1;
                height: 1px;
                background: linear-gradient(90deg, transparent, var(--border), transparent);
            }
            
            .signin-divider-text {
                font-size: 12px;
                font-weight: 500;
                color: var(--text-muted);
                text-transform: uppercase;
                letter-spacing: 1px;
            }
            
            /* Social Buttons */
            .signin-social-buttons {
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 12px;
            }
            
            .signin-btn-social {
                height: 44px;
                background: var(--glass-heavy);
                border: 1px solid var(--border);
                border-radius: 12px;
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 8px;
                font-size: 14px;
                font-weight: 600;
                color: var(--text-primary);
                cursor: pointer;
                transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            }
            
            .signin-btn-social:hover {
                background: rgba(255, 255, 255, 0.08);
                border-color: var(--border-strong);
                transform: translateY(-1px);
            }
            
            .signin-social-icon {
                width: 18px;
                height: 18px;
            }
            
            /* Footer */
            .signin-footer {
                text-align: center;
                margin-top: 32px;
                padding-top: 24px;
                border-top: 1px solid var(--border);
            }
            
            .signin-footer-text {
                font-size: 14px;
                color: var(--text-secondary);
            }
            
            .signin-footer-link {
                color: var(--primary);
                text-decoration: none;
                font-weight: 600;
                transition: color 0.2s ease;
            }
            
            .signin-footer-link:hover {
                color: var(--primary-dark);
            }
            
            /* Security Badge */
            .signin-security-badge {
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 8px;
                margin-top: 24px;
                padding-top: 24px;
                border-top: 1px solid var(--border);
                font-size: 12px;
                color: var(--text-muted);
            }
            
            .signin-security-icon {
                width: 16px;
                height: 16px;
                color: var(--success);
            }
            
            /* Loading State */
            .signin-loading {
                display: none;
                width: 18px;
                height: 18px;
                border: 2px solid var(--background);
                border-top-color: transparent;
                border-radius: 50%;
                animation: spin 0.8s linear infinite;
            }
            
            @keyframes spin {
                to { transform: rotate(360deg); }
            }
            
            .signin-btn-submit.loading-state .signin-btn-text {
                display: none;
            }
            
            .signin-btn-submit.loading-state .signin-loading {
                display: inline-block;
            }
            
            /* Enterprise Features */
            .signin-enterprise-features {
                position: fixed;
                bottom: 32px;
                left: 50%;
                transform: translateX(-50%);
                display: flex;
                gap: 24px;
                font-size: 12px;
                color: var(--text-muted);
            }
            
            .signin-enterprise-features a {
                color: var(--text-muted);
                text-decoration: none;
                transition: color 0.2s ease;
            }
            
            .signin-enterprise-features a:hover {
                color: var(--text-secondary);
            }

            /* ====== SIGN UP STYLES (800px wide) ====== */
            
            /* Premium Background */
            .signup-background {
                position: fixed;
                inset: 0;
                background: 
                    radial-gradient(circle at 30% 20%, rgba(0, 212, 255, 0.08) 0%, transparent 50%),
                    radial-gradient(circle at 70% 60%, rgba(14, 165, 233, 0.06) 0%, transparent 50%),
                    radial-gradient(circle at 50% 50%, var(--background-secondary) 0%, var(--background) 100%);
                z-index: -2;
            }
            
            /* Animated Orbs */
            .signup-orb {
                position: fixed;
                border-radius: 50%;
                filter: blur(80px);
                opacity: 0.5;
                animation: float 20s infinite ease-in-out;
                z-index: -1;
            }
            
            .signup-orb-1 {
                width: 400px;
                height: 400px;
                background: var(--primary);
                top: -200px;
                left: -200px;
                animation-delay: 0s;
            }
            
            .signup-orb-2 {
                width: 300px;
                height: 300px;
                background: var(--primary-dark);
                bottom: -150px;
                right: -150px;
                animation-delay: 10s;
            }
            
            @keyframes float {
                0%, 100% {
                    transform: translate(0, 0) scale(1);
                }
                33% {
                    transform: translate(30px, -30px) scale(1.1);
                }
                66% {
                    transform: translate(-20px, 20px) scale(0.9);
                }
            }
            
            /* Sign Up Card - Wide Layout */
            .signup-card {
                width: 100%;
                max-width: 800px;
                background: var(--surface);
                backdrop-filter: blur(40px);
                -webkit-backdrop-filter: blur(40px);
                border: 1px solid var(--border);
                border-radius: 24px;
                overflow: hidden;
                box-shadow: 
                    var(--shadow-xl),
                    var(--shadow-glow),
                    inset 0 1px 1px rgba(255, 255, 255, 0.05);
                animation: cardEntry 0.8s cubic-bezier(0.21, 1.02, 0.73, 1);
            }
            
            /* Card Header */
            .signup-card-header {
                padding: 32px 48px;
                background: linear-gradient(135deg, var(--glass) 0%, var(--glass-heavy) 100%);
                border-bottom: 1px solid var(--border);
            }
            
            .signup-header-content {
                display: flex;
                align-items: center;
                justify-content: space-between;
            }
            
            .signup-logo-section {
                display: flex;
                align-items: center;
                gap: 16px;
            }
            
            .signup-logo {
                width: 48px;
                height: 48px;
                background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
                border-radius: 14px;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 24px;
                font-weight: 800;
                color: var(--background);
                box-shadow: 0 8px 24px rgba(var(--primary-rgb), 0.3);
            }
            
            .signup-brand-name {
                font-size: 20px;
                font-weight: 700;
                letter-spacing: -0.5px;
            }
            
            .signup-close-btn {
                width: 36px;
                height: 36px;
                background: var(--glass-heavy);
                border: 1px solid var(--border);
                border-radius: 10px;
                display: flex;
                align-items: center;
                justify-content: center;
                color: var(--text-muted);
                cursor: pointer;
                transition: all 0.2s ease;
            }
            
            .signup-close-btn:hover {
                background: rgba(255, 255, 255, 0.08);
                color: var(--text-secondary);
                transform: rotate(90deg);
            }
            
            /* Progress Bar */
            .signup-progress-bar {
                padding: 24px 48px;
                background: var(--glass);
                border-bottom: 1px solid var(--border);
            }
            
            .signup-progress-steps {
                display: flex;
                justify-content: space-between;
                position: relative;
            }
            
            .signup-progress-line {
                position: absolute;
                top: 20px;
                left: 0;
                right: 0;
                height: 2px;
                background: var(--border);
                z-index: 0;
            }
            
            .signup-progress-line-fill {
                position: absolute;
                top: 0;
                left: 0;
                height: 100%;
                background: linear-gradient(90deg, var(--primary) 0%, var(--primary-dark) 100%);
                transition: width 0.5s cubic-bezier(0.4, 0, 0.2, 1);
            }
            
            .signup-step {
                position: relative;
                z-index: 1;
                text-align: center;
                flex: 1;
            }
            
            .signup-step-number {
                width: 40px;
                height: 40px;
                margin: 0 auto 8px;
                background: var(--background);
                border: 2px solid var(--border);
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 14px;
                font-weight: 700;
                transition: all 0.3s ease;
            }
            
            .signup-step.active .signup-step-number {
                background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
                border-color: var(--primary);
                color: var(--background);
                transform: scale(1.1);
            }
            
            .signup-step.completed .signup-step-number {
                background: var(--success);
                border-color: var(--success);
                color: white;
            }
            
            .signup-step.completed .signup-step-number::after {
                content: '✓';
                position: absolute;
                font-size: 16px;
            }
            
            .signup-step.completed .signup-step-number span {
                display: none;
            }
            
            .signup-step-label {
                font-size: 12px;
                font-weight: 500;
                color: var(--text-muted);
            }
            
            .signup-step.active .signup-step-label {
                color: var(--primary);
            }
            
            .signup-step.completed .signup-step-label {
                color: var(--success);
            }
            
            /* Card Body */
            .signup-card-body {
                padding: 48px;
            }
            
            /* Form Steps */
            .signup-form-step {
                display: none;
                animation: fadeIn 0.4s ease;
            }
            
            .signup-form-step.active {
                display: block;
            }
            
            .signup-step-title {
                font-size: 28px;
                font-weight: 600;
                letter-spacing: -0.5px;
                margin-bottom: 8px;
            }
            
            .signup-step-description {
                font-size: 14px;
                color: var(--text-secondary);
                margin-bottom: 32px;
            }
            
            /* Form Layout */
            .signup-form-row {
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 20px;
            }
            
            .signup-form-group {
                margin-bottom: 24px;
            }
            
            .signup-form-group.signup-full-width {
                grid-column: 1 / -1;
            }
            
            .signup-form-label {
                display: block;
                font-size: 12px;
                font-weight: 600;
                color: var(--text-secondary);
                text-transform: uppercase;
                letter-spacing: 0.8px;
                margin-bottom: 8px;
            }
            
            .signup-required {
                color: var(--error);
            }
            
            .signup-input-wrapper {
                position: relative;
            }
            
            .signup-form-input {
                width: 100%;
                height: 48px;
                padding: 0 16px;
                background: var(--glass-heavy);
                border: 1px solid var(--border);
                border-radius: 12px;
                color: var(--text-primary);
                font-size: 15px;
                font-weight: 400;
                transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            }
            
            .signup-form-input:hover {
                background: rgba(255, 255, 255, 0.06);
            }
            
            .signup-form-input:focus {
                outline: none;
                background: rgba(255, 255, 255, 0.08);
                border-color: var(--primary);
                box-shadow: 0 0 0 3px rgba(var(--primary-rgb), 0.1);
            }
            
            .signup-form-input.signup-with-icon {
                padding-left: 44px;
            }
            
            .signup-input-icon {
                position: absolute;
                left: 16px;
                top: 50%;
                transform: translateY(-50%);
                width: 18px;
                height: 18px;
                color: var(--text-muted);
            }
            
            /* Company Selection */
            .signup-company-grid {
                display: grid;
                grid-template-columns: repeat(4, 1fr);
                gap: 16px;
                margin-bottom: 24px;
            }
            
            .signup-company-card {
                padding: 20px;
                background: var(--glass);
                border: 2px solid var(--border);
                border-radius: 12px;
                text-align: center;
                cursor: pointer;
                transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            }
            
            .signup-company-card:hover {
                background: var(--glass-heavy);
                transform: translateY(-2px);
            }
            
            .signup-company-card.selected {
                background: linear-gradient(135deg, rgba(var(--primary-rgb), 0.1) 0%, rgba(var(--primary-rgb), 0.05) 100%);
                border-color: var(--primary);
            }
            
            .signup-company-icon {
                font-size: 24px;
                margin-bottom: 8px;
            }
            
            .signup-company-name {
                font-size: 14px;
                font-weight: 600;
                margin-bottom: 4px;
            }
            
            .signup-company-type {
                font-size: 11px;
                color: var(--text-muted);
            }
            
            /* Password Requirements */
            .signup-password-requirements {
                margin-top: 12px;
                padding: 12px;
                background: var(--glass);
                border: 1px solid var(--border);
                border-radius: 10px;
            }
            
            .signup-requirement {
                display: flex;
                align-items: center;
                gap: 8px;
                font-size: 12px;
                color: var(--text-muted);
                margin-bottom: 6px;
            }
            
            .signup-requirement:last-child {
                margin-bottom: 0;
            }
            
            .signup-requirement.met {
                color: var(--success);
            }
            
            .signup-requirement-icon {
                width: 16px;
                height: 16px;
            }
            
            /* Navigation Buttons */
            .signup-form-navigation {
                display: flex;
                justify-content: space-between;
                gap: 16px;
                margin-top: 40px;
                padding-top: 32px;
                border-top: 1px solid var(--border);
            }
            
            .signup-btn {
                height: 48px;
                padding: 0 32px;
                border-radius: 12px;
                font-size: 15px;
                font-weight: 600;
                cursor: pointer;
                transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                border: none;
            }
            
            .signup-btn-secondary {
                background: var(--glass-heavy);
                border: 1px solid var(--border);
                color: var(--text-primary);
            }
            
            .signup-btn-secondary:hover {
                background: rgba(255, 255, 255, 0.08);
                border-color: var(--border-strong);
            }
            
            .signup-btn-primary {
                background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
                color: var(--background);
                box-shadow: 0 4px 14px rgba(var(--primary-rgb), 0.4);
                margin-left: auto;
            }
            
            .signup-btn-primary:hover {
                transform: translateY(-1px);
                box-shadow: 0 6px 20px rgba(var(--primary-rgb), 0.5);
            }
            
            .signup-btn-primary:active {
                transform: translateY(0);
            }
            
            /* Footer */
            .signup-card-footer {
                padding: 24px 48px;
                background: var(--glass);
                border-top: 1px solid var(--border);
                text-align: center;
            }
            
            .signup-footer-text {
                font-size: 14px;
                color: var(--text-secondary);
            }
            
            .signup-footer-link {
                color: var(--primary);
                text-decoration: none;
                font-weight: 600;
                transition: color 0.2s ease;
            }
            
            .signup-footer-link:hover {
                color: var(--primary-dark);
            }
            
            /* Success Message */
            .signup-success-message {
                text-align: center;
                padding: 60px;
            }
            
            .signup-success-icon {
                width: 80px;
                height: 80px;
                margin: 0 auto 24px;
                background: linear-gradient(135deg, var(--success) 0%, #059669 100%);
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 40px;
                color: white;
                animation: successPop 0.5s cubic-bezier(0.21, 1.02, 0.73, 1);
            }
            
            @keyframes successPop {
                0% {
                    transform: scale(0);
                }
                50% {
                    transform: scale(1.2);
                }
                100% {
                    transform: scale(1);
                }
            }
            
            /* Responsive */
            @media (max-width: 768px) {
                .signup-card-body {
                    padding: 32px 24px;
                }
                
                .signup-form-row {
                    grid-template-columns: 1fr;
                }
                
                .signup-company-grid {
                    grid-template-columns: repeat(2, 1fr);
                }
                
                .signup-progress-bar {
                    padding: 20px 24px;
                }
                
                .signup-step-label {
                    display: none;
                }
            }
            
            @media (max-width: 540px) {
                .signin-card {
                    margin: 20px;
                    padding: 32px 24px;
                }
                
                .signin-social-buttons {
                    grid-template-columns: 1fr;
                }
                
                .signin-enterprise-features {
                    flex-direction: column;
                    align-items: center;
                    gap: 8px;
                }
            }
        `;
        document.head.appendChild(style);
    }

    bindEvents() {
        // Sign in form
        const signInForm = document.getElementById('signInForm');
        if (signInForm) {
            signInForm.addEventListener('submit', this.handleSignIn.bind(this));
        }

        // Password strength for signup
        const signUpPassword = document.getElementById('signUpPassword');
        if (signUpPassword) {
            signUpPassword.addEventListener('input', this.updatePasswordRequirements.bind(this));
        }

        // Form validation
        this.addFormValidation();

        // Keyboard shortcuts
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') {
                this.closeDialog();
            }
        });

        // Backdrop clicks
        document.querySelectorAll('.auth-overlay').forEach(overlay => {
            overlay.addEventListener('click', (e) => {
                if (e.target === overlay) {
                    this.closeDialog();
                }
            });
        });
    }

    addFormValidation() {
        const inputs = document.querySelectorAll('.signin-form-input, .signup-form-input');
        inputs.forEach(input => {
            input.addEventListener('blur', this.validateField.bind(this));
            input.addEventListener('focus', this.clearFieldError.bind(this));
        });
    }

    validateField(e) {
        const field = e.target;
        const value = field.value.trim();
        const fieldId = field.id;
        
        let isValid = true;
        let errorMessage = '';

        if (field.required && !value) {
            isValid = false;
            errorMessage = 'This field is required';
        } else if (fieldId.includes('Email') && value) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(value)) {
                isValid = false;
                errorMessage = 'Please enter a valid email address';
            }
        } else if (fieldId.includes('Password') && value) {
            if (value.length < 6) {
                isValid = false;
                errorMessage = 'Password must be at least 6 characters';
            }
        }

        const errorElement = document.getElementById(fieldId + 'Error');
        if (errorElement) {
            if (isValid) {
                field.classList.remove('error');
                errorElement.textContent = '';
            } else {
                field.classList.add('error');
                errorElement.textContent = '⚠️ ' + errorMessage;
            }
        }

        return isValid;
    }

    clearFieldError(e) {
        const field = e.target;
        field.classList.remove('error');
        const errorElement = document.getElementById(field.id + 'Error');
        if (errorElement) {
            errorElement.textContent = '';
        }
    }

    updatePasswordRequirements(e) {
        const password = e.target.value;
        
        // Length requirement
        const lengthReq = document.getElementById('signupReqLength');
        if (lengthReq) {
            if (password.length >= 12) {
                lengthReq.classList.add('met');
                lengthReq.querySelector('svg').innerHTML = '<path d="M5 13l4 4L19 7" stroke="currentColor" stroke-width="2" fill="none"/>';
            } else {
                lengthReq.classList.remove('met');
                lengthReq.querySelector('svg').innerHTML = '<circle cx="10" cy="10" r="8" fill="none" stroke="currentColor" stroke-width="2"/>';
            }
        }
        
        // Uppercase requirement
        const upperReq = document.getElementById('signupReqUpper');
        if (upperReq) {
            if (/[A-Z]/.test(password)) {
                upperReq.classList.add('met');
                upperReq.querySelector('svg').innerHTML = '<path d="M5 13l4 4L19 7" stroke="currentColor" stroke-width="2" fill="none"/>';
            } else {
                upperReq.classList.remove('met');
                upperReq.querySelector('svg').innerHTML = '<circle cx="10" cy="10" r="8" fill="none" stroke="currentColor" stroke-width="2"/>';
            }
        }
        
        // Number/symbol requirement
        const numberReq = document.getElementById('signupReqNumber');
        if (numberReq) {
            if (/[0-9!@#$%^&*]/.test(password)) {
                numberReq.classList.add('met');
                numberReq.querySelector('svg').innerHTML = '<path d="M5 13l4 4L19 7" stroke="currentColor" stroke-width="2" fill="none"/>';
            } else {
                numberReq.classList.remove('met');
                numberReq.querySelector('svg').innerHTML = '<circle cx="10" cy="10" r="8" fill="none" stroke="currentColor" stroke-width="2"/>';
            }
        }
    }

    selectCompany(element) {
        // Remove selected class from all company cards
        document.querySelectorAll('.signup-company-card').forEach(card => {
            card.classList.remove('selected');
        });
        
        // Add selected class to clicked card
        element.classList.add('selected');
        
        // Store selected company
        this.signupData.company = element.dataset.company;
        console.log('Company selected:', this.signupData.company);
    }

    togglePassword() {
        const passwordInput = document.getElementById('signInPassword');
        const eyeIcon = document.getElementById('signInEyeIcon');
        
        if (passwordInput && eyeIcon) {
            if (passwordInput.type === 'password') {
                passwordInput.type = 'text';
                eyeIcon.innerHTML = `
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"/>
                `;
            } else {
                passwordInput.type = 'password';
                eyeIcon.innerHTML = `
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                `;
            }
        }
    }

    nextStep() {
        const totalSteps = 4;
        
        if (this.currentStep < totalSteps) {
            const prevStep = this.currentStep;
            this.currentStep++;
            this.updateStepDisplay(prevStep);
        }
    }

    prevStep() {
        if (this.currentStep > 1) {
            const prevStep = this.currentStep;
            this.currentStep--;
            this.updateStepDisplay(prevStep);
        }
    }
    
    updateStepDisplay(prevStep) {
        const totalSteps = 4;
        
        // Update form steps
        const prevFormStep = document.getElementById(`signupFormStep${prevStep}`);
        const currentFormStep = document.getElementById(`signupFormStep${this.currentStep}`);
        
        if (prevFormStep) prevFormStep.classList.remove('active');
        if (currentFormStep) currentFormStep.classList.add('active');
        
        // Update progress indicators
        const prevProgressStep = document.getElementById(`signupStep${prevStep}`);
        const currentProgressStep = document.getElementById(`signupStep${this.currentStep}`);
        
        if (prevProgressStep) prevProgressStep.classList.remove('active');
        if (currentProgressStep) currentProgressStep.classList.add('active');
        
        // Mark completed steps
        if (this.currentStep > prevStep && prevProgressStep) {
            prevProgressStep.classList.add('completed');
        } else if (this.currentStep < prevStep && prevProgressStep) {
            prevProgressStep.classList.remove('completed');
        }
        
        // Update progress bar
        const progressPercent = ((this.currentStep - 1) / (totalSteps - 1)) * 100;
        const progressFill = document.getElementById('signupProgressFill');
        if (progressFill) {
            progressFill.style.width = progressPercent + '%';
        }
        
        // Update navigation buttons
        const prevBtn = document.getElementById('signupPrevBtn');
        const nextBtn = document.getElementById('signupNextBtn');
        const navigation = document.getElementById('signupNavigation');
        
        if (prevBtn) {
            prevBtn.style.display = this.currentStep === 1 ? 'none' : 'block';
        }
        
        if (nextBtn && navigation) {
            if (this.currentStep === totalSteps) {
                navigation.style.display = 'none';
            } else {
                navigation.style.display = 'flex';
                nextBtn.textContent = this.currentStep === totalSteps - 1 ? 'Complete' : 'Continue';
            }
        }
    }

    async handleSignIn(e) {
        e.preventDefault();
        
        const email = document.getElementById('signInEmail').value.trim();
        const password = document.getElementById('signInPassword').value;
        const submitBtn = document.querySelector('.signin-btn-submit');

        if (!email || !password) {
            alert('Please fill in all fields');
            return;
        }

        try {
            submitBtn.classList.add('loading-state');

            // PRESERVE: Use existing Firebase auth
            const userCredential = await auth.signInWithEmailAndPassword(email, password);
            console.log('✅ User signed in successfully:', userCredential.user.email);

            // Close dialog
            this.closeDialog();

        } catch (error) {
            console.error('❌ Sign in error:', error);
            alert('Sign in failed: ' + error.message);
        } finally {
            submitBtn.classList.remove('loading-state');
        }
    }

    showSignIn() {
        console.log('🎨 showSignIn() called - showing professional sign in dialog');
        
        // Hide all overlays first
        document.querySelectorAll('.auth-overlay').forEach(overlay => {
            overlay.classList.remove('active');
        });
        
        // Show sign in overlay
        const signInOverlay = document.getElementById('signInOverlay');
        if (signInOverlay) {
            // CRITICAL: Remove any inline styles that might be blocking visibility
            signInOverlay.style.display = '';
            signInOverlay.style.visibility = '';
            signInOverlay.style.opacity = '';
            
            signInOverlay.classList.add('active');
            console.log('✅ Added active class to sign in overlay and cleared inline styles');
            
            // Focus email input
            setTimeout(() => {
                const emailInput = document.getElementById('signInEmail');
                if (emailInput) {
                    emailInput.focus();
                    console.log('✅ Email input focused');
                }
            }, 100);
        }
    }

    showSignUp() {
        console.log('🎨 showSignUp() called - showing professional sign up dialog');
        
        // Hide all overlays first
        document.querySelectorAll('.auth-overlay').forEach(overlay => {
            overlay.classList.remove('active');
        });
        
        // Reset to step 1
        this.currentStep = 1;
        this.updateStepDisplay(1);
        
        // Show sign up overlay
        const signUpOverlay = document.getElementById('signUpOverlay');
        if (signUpOverlay) {
            signUpOverlay.classList.add('active');
            console.log('✅ Added active class to sign up overlay');
        }
    }

    closeDialog() {
        console.log('🎨 closeDialog() called');
        
        // Hide all overlays
        document.querySelectorAll('.auth-overlay').forEach(overlay => {
            overlay.classList.remove('active');
        });
        
        // Clear all form fields
        document.querySelectorAll('.signin-form-input, .signup-form-input').forEach(input => {
            input.value = '';
            input.classList.remove('error');
        });
    }
}

// Initialize and make globally available
let authDialogs;

// CRITICAL: Make the class available globally for direct instantiation
window.AuthenticationDialogs = AuthenticationDialogs;

// Manual initialization function for Enterprise services
window.initializeAuthDialogs = function() {
    if (!authDialogs) {
        console.log('🔧 Manually initializing Clean Professional Authentication Dialogs');
        authDialogs = new AuthenticationDialogs();
        window.authDialogs = authDialogs;
        console.log('✅ Clean Professional Authentication Dialogs initialized manually');
    } else {
        console.log('✅ Clean Professional Authentication Dialogs already initialized');
    }
    return authDialogs;
};

document.addEventListener('DOMContentLoaded', () => {
    if (!authDialogs) {
        authDialogs = new AuthenticationDialogs();
        window.authDialogs = authDialogs;
        console.log('🎨 Clean Professional Authentication Dialogs ready via DOMContentLoaded');
    }
});

// Immediate initialization if DOM is already ready
if (document.readyState === 'loading') {
    // DOM is still loading, wait for DOMContentLoaded
} else {
    // DOM is already loaded, initialize immediately
    setTimeout(() => {
        if (!authDialogs) {
            window.initializeAuthDialogs();
        }
    }, 100);
}

export default AuthenticationDialogs;

